import { createReducer, on } from '@ngrx/store'
import { EVisitorState, EVisitorStatusType, EVisitorActionType } from './evisitor.model'
import * as EVisitorActions from './evisitor.actions'

export const initialState: EVisitorState = {
  statuses: {},
  pendingOperations: {},
  error: {},
}

export const evisitorReducer = createReducer(
  initialState,

  // Check-in to eVisitor
  on(EVisitorActions.CheckInToEVisitor, (state, { bookingId }) => ({
    ...state,
    pendingOperations: {
      ...state.pendingOperations,
      [bookingId]: true,
    },
    statuses: {
      ...state.statuses,
      [bookingId]: {
        bookingId,
        status: EVisitorStatusType.Pending,
        lastAction: EVisitorActionType.CheckIn,
        lastAttemptTime: new Date().toISOString(),
      },
    },
    error: {
      ...state.error,
      [bookingId]: null,
    },
  })),

  on(EVisitorActions.CheckInToEVisitorSuccess, (state, { bookingId, response }) => ({
    ...state,
    pendingOperations: {
      ...state.pendingOperations,
      [bookingId]: false,
    },
    statuses: {
      ...state.statuses,
      [bookingId]: {
        bookingId,
        status: EVisitorStatusType.Sent,
        lastAction: EVisitorActionType.CheckIn,
        lastAttemptTime: new Date().toISOString(),
        referenceId: response.referenceId,
      },
    },
  })),

  on(EVisitorActions.CheckInToEVisitorFailure, (state, { bookingId, error }) => ({
    ...state,
    pendingOperations: {
      ...state.pendingOperations,
      [bookingId]: false,
    },
    statuses: {
      ...state.statuses,
      [bookingId]: {
        bookingId,
        status: EVisitorStatusType.Error,
        lastAction: EVisitorActionType.CheckIn,
        lastAttemptTime: new Date().toISOString(),
        errorMessage: error?.error?.message || error?.message || 'Unknown error',
      },
    },
    error: {
      ...state.error,
      [bookingId]: error?.error?.message || error?.message || 'Unknown error',
    },
  })),

  // Check-out from eVisitor
  on(EVisitorActions.CheckOutFromEVisitor, (state, { bookingId }) => ({
    ...state,
    pendingOperations: {
      ...state.pendingOperations,
      [bookingId]: true,
    },
    statuses: {
      ...state.statuses,
      [bookingId]: {
        ...state.statuses[bookingId],
        status: EVisitorStatusType.Pending,
        lastAction: EVisitorActionType.Deregister,
        lastAttemptTime: new Date().toISOString(),
      },
    },
    error: {
      ...state.error,
      [bookingId]: null,
    },
  })),

  on(EVisitorActions.CheckOutFromEVisitorSuccess, (state, { bookingId, response }) => ({
    ...state,
    pendingOperations: {
      ...state.pendingOperations,
      [bookingId]: false,
    },
    statuses: {
      ...state.statuses,
      [bookingId]: {
        ...state.statuses[bookingId],
        status: EVisitorStatusType.Deregistered,
        lastAction: EVisitorActionType.Deregister,
        lastAttemptTime: new Date().toISOString(),
      },
    },
  })),

  on(EVisitorActions.CheckOutFromEVisitorFailure, (state, { bookingId, error }) => ({
    ...state,
    pendingOperations: {
      ...state.pendingOperations,
      [bookingId]: false,
    },
    statuses: {
      ...state.statuses,
      [bookingId]: {
        ...state.statuses[bookingId],
        status: EVisitorStatusType.Error,
        lastAction: EVisitorActionType.Deregister,
        lastAttemptTime: new Date().toISOString(),
        errorMessage: error?.error?.message || error?.message || 'Unknown error',
      },
    },
    error: {
      ...state.error,
      [bookingId]: error?.error?.message || error?.message || 'Unknown error',
    },
  })),

  // Cancel eVisitor Registration
  on(EVisitorActions.CancelEVisitorRegistration, (state, { bookingId }) => ({
    ...state,
    pendingOperations: {
      ...state.pendingOperations,
      [bookingId]: true,
    },
    statuses: {
      ...state.statuses,
      [bookingId]: {
        ...state.statuses[bookingId],
        status: EVisitorStatusType.Pending,
        lastAction: EVisitorActionType.Cancel,
        lastAttemptTime: new Date().toISOString(),
      },
    },
    error: {
      ...state.error,
      [bookingId]: null,
    },
  })),

  on(EVisitorActions.CancelEVisitorRegistrationSuccess, (state, { bookingId, response }) => ({
    ...state,
    pendingOperations: {
      ...state.pendingOperations,
      [bookingId]: false,
    },
    statuses: {
      ...state.statuses,
      [bookingId]: {
        ...state.statuses[bookingId],
        status: EVisitorStatusType.Canceled,
        lastAction: EVisitorActionType.Cancel,
        lastAttemptTime: new Date().toISOString(),
        referenceId: undefined, // Clear reference after cancellation
      },
    },
  })),

  on(EVisitorActions.CancelEVisitorRegistrationFailure, (state, { bookingId, error }) => ({
    ...state,
    pendingOperations: {
      ...state.pendingOperations,
      [bookingId]: false,
    },
    statuses: {
      ...state.statuses,
      [bookingId]: {
        ...state.statuses[bookingId],
        status: EVisitorStatusType.Error,
        lastAction: EVisitorActionType.Cancel,
        lastAttemptTime: new Date().toISOString(),
        errorMessage: error?.error?.message || error?.message || 'Unknown error',
      },
    },
    error: {
      ...state.error,
      [bookingId]: error?.error?.message || error?.message || 'Unknown error',
    },
  })),

  // Clear state
  on(EVisitorActions.ClearEVisitorState, (state, { bookingId }) => {
    const { [bookingId]: _status, ...remainingStatuses } = state.statuses
    const { [bookingId]: _pending, ...remainingPending } = state.pendingOperations
    const { [bookingId]: _error, ...remainingErrors } = state.error

    return {
      statuses: remainingStatuses,
      pendingOperations: remainingPending,
      error: remainingErrors,
    }
  })
)
